const SESSION_SCOPED_KEYS = [
  'sessionId',
  'sessionToken',
  'assessment_state',
  'assessment_user_info',
  'isDemo',
  'voucherId',
  'assessment_resume_email_sent_to',
];

const VOUCHER_RECOVERY_KEY = 'assessment_voucher_recovery';
const VOUCHER_RECOVERY_TTL_MS = 7 * 24 * 60 * 60 * 1000;

// The voucher code/link is a bearer credential, so retain it only for a short
// window. It is kept separately from sessionStorage so a Safari tab restart or
// lost session token can re-run /assessment/start for the SAME voucher. Never
// use this to create a new anonymous assessment.
export function rememberVoucherCredential(kind, value, voucherId, storage = localStorage, now = Date.now()) {
  if (!['code', 'token'].includes(kind) || !value || !voucherId) return false;
  try {
    storage.setItem(VOUCHER_RECOVERY_KEY, JSON.stringify({
      kind,
      value,
      voucherId,
      expiresAt: now + VOUCHER_RECOVERY_TTL_MS,
    }));
    return true;
  } catch {
    // Private browsing can disallow persistent storage; manual re-entry remains available.
    return false;
  }
}

export function getRememberedVoucherCredential(storage = localStorage, now = Date.now()) {
  try {
    const raw = storage.getItem(VOUCHER_RECOVERY_KEY);
    if (!raw) return null;
    const saved = JSON.parse(raw);
    if (!['code', 'token'].includes(saved?.kind)
      || !saved.value || !saved.voucherId || !Number.isFinite(saved.expiresAt)
      || saved.expiresAt <= now) {
      storage.removeItem(VOUCHER_RECOVERY_KEY);
      return null;
    }
    return saved;
  } catch {
    return null;
  }
}

export function forgetRememberedVoucherCredential(storage = localStorage) {
  try { storage.removeItem(VOUCHER_RECOVERY_KEY); } catch { /* storage unavailable */ }
}

export async function recoverRememberedVoucher(redeem, storage = localStorage, sessionStore = sessionStorage) {
  const saved = getRememberedVoucherCredential(storage);
  if (!saved) return null;
  const activeVoucherId = sessionStore.getItem('voucherId');
  if ((activeVoucherId && activeVoucherId !== saved.voucherId)
    || (!activeVoucherId && sessionStore.getItem('sessionId'))) return null;
  const result = await redeem(saved.kind, saved.value);
  const current = getRememberedVoucherCredential(storage);
  // Another tab may have redeemed a different voucher while the request was
  // pending. Do not install the old response over its new credentials.
  if (!current || current.kind !== saved.kind || current.value !== saved.value
    || current.voucherId !== saved.voucherId) return null;
  if (result?.reviewMode === 'awaiting_email' || result?.reviewMode === 'email_mismatch') return result;
  if (!result?.sessionId || !result?.sessionToken || result?.voucherId !== saved.voucherId) {
    forgetRememberedVoucherCredential(storage);
    return null;
  }
  storeRedeemedAssessmentSession(result, sessionStore);
  return result;
}

// A redeem link is authoritative for the assessment it names. When it opens a
// different session in the same browser, remove the prior assessment's local
// progress and identity before installing the new credentials. Keep language
// preference (`assessment_lang`) because that belongs to the browser, not the
// assessment session.
export function storeRedeemedAssessmentSession(result, storage = sessionStorage) {
  const nextSessionId = result?.sessionId;
  const nextSessionToken = result?.sessionToken;
  if (!nextSessionId || !nextSessionToken) return false;

  const previousSessionId = storage.getItem('sessionId');
  const switchedSession = previousSessionId !== nextSessionId;
  if (switchedSession) {
    SESSION_SCOPED_KEYS.forEach((key) => storage.removeItem(key));
  }

  storage.setItem('sessionId', nextSessionId);
  storage.setItem('sessionToken', nextSessionToken);
  if (result.voucherId) storage.setItem('voucherId', result.voucherId);
  return switchedSession;
}
